<template>
  <div>
    <finished-documentation v-if="sucessSubmitFiles" />
    <div
      class="w-full flex justify-center"
      v-else
    >
      <jade-page-container size="wide">
        <template>
          <jade-box class="lg:flex-[2]">
            <div class="flex flex-col gap-3 p-5">
              <jade-text
                variant="heading-xsmall"
                color="high"
                >{{ $t('send.document') }}</jade-text
              >
              <jade-text
                color="medium"
                weight="medium"
                >{{ $t('paragraph.1') }}
                <span class="text-jade-content-high">{{
                  $t('paragraph.2')
                }}</span></jade-text
              >

              <jade-link
                external
                :href="$sanitize(chargebackDocumentationUrl)"
                target="_blank"
                >{{ $t('documents-list.link') }}</jade-link
              >

              <file-drag-and-drop
                :files.sync="files"
                :limit="50"
              />
              <div
                v-for="(fileItem, index) in filesItems"
                :key="`${fileItem.name}-${index}`"
              >
                <file-list-item
                  :file="fileItem"
                  :fileIsLoading="filesIsLoading"
                  @deleteFile="deleteFile"
                />
                <transaction-item
                  v-if="transactionItem(fileItem.name)"
                  :transaction="transactionItem(fileItem.name)"
                />
              </div>
            </div>
          </jade-box>
        </template>
        <template #aside>
          <div class="flex flex-col flex-1 gap-6">
            <instructions />
            <jade-alert
              v-if="totalSizeError"
              variant="negative"
              showIcon
            >
              {{ $t('total.size.error') }}
            </jade-alert>
            <jade-button
              variant="primary-solid"
              :loading="submitFilesIsLoading"
              :disabled="sendDocumentationIsDisabled"
              @click="submitFiles"
              >{{ $t('next') }}</jade-button
            >
          </div>
        </template>
      </jade-page-container>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import {
  JadeAlert,
  JadeButton,
  JadeText,
  JadeBox,
  JadeLink,
  JadePageContainer
} from '@jade'
import FileDragAndDrop from '../components/FileDragAndDrop'
import FinishedDocumentation from '../components/FinishedDocumentation'
import FileListItem from '../components/FileListItem'
import Instructions from './components/Instructions'
import TransactionItem from './components/TransactionItem'

import ChargebackManager from '../chargeback.manager'

import { EXTERNAL_ENDPOINTS } from '@/external-endpoints'
import ChargebackMassRepresentmentManager from './chargeback-mass-representment.manager'

export default {
  name: 'ChargeBackMassRepresentment',
  components: {
    JadeAlert,
    JadeButton,
    JadeText,
    JadeBox,
    JadeLink,
    JadePageContainer,
    FileListItem,
    FinishedDocumentation,
    FileDragAndDrop,
    Instructions,
    TransactionItem
  },
  data () {
    return {
      files: [],
      filesItems: null,
      filesIsLoading: false,
      submitFilesIsLoading: false,
      sucessSubmitFiles: false,
      hasAnyError: false,
      totalSizeError: false,
      transactions: []
    }
  },
  computed: {
    hasAnyFile () {
      return this.files.length > 0
    },
    sendDocumentationIsDisabled () {
      return !this.hasAnyFile || this.filesIsLoading || !!this.hasAnyError
    },
    chargebackDocumentationUrl () {
      return EXTERNAL_ENDPOINTS.CHARGEBACK_RECOMMENDATION_DOC.url()
    }
  },
  watch: {
    files: {
      deep: true,
      handler (newFiles) {
        if (newFiles.length > 0) {
          this.sendFilesToValidate(newFiles)
        }
      }
    }
  },
  methods: {
    ...mapActions({
      validateFiles: 'VALIDATE_FILES_BATCH',
      processFiles: 'PROCESS_FILE_BATCH',
      getAllChargebacks: 'GET_ALL_CHARGEBACKS'
    }),
    deleteFile (fileToRemove) {
      const fileIndex = this.files.findIndex(
        file => file.name === fileToRemove.name
      )
      if (fileIndex !== -1) {
        this.files.splice(fileIndex, 1)
        this.filesItems.splice(fileIndex, 1)
      }
    },
    async getTransactionByIds (transactionIds) {
      if (transactionIds.length === 0) {
        this.transactions = []
        return
      }

      const { data: trasactionsData } = await this.getAllChargebacks({
        transactionId: transactionIds
      })

      this.transactions = trasactionsData
    },

    processValidateResponse (validateResponse) {
      if (!validateResponse) return
      this.totalSizeError = !validateResponse.is_valid_size
      this.hasAnyError =
        !!validateResponse.validation_error.length ||
        this.totalSizeError ||
        !validateResponse.files_are_valid

      this.filesItems = ChargebackManager.getFilesWithErrors(
        this.files,
        validateResponse.files
      )

      this.getTransactionByIds(validateResponse.transactions_found)
    },
    sendFilesToValidate () {
      this.filesIsLoading = true
      const filesToValidate = ChargebackManager.prepareFilesToValidate(
        this.files
      )

      this.filesItems = this.files.map(file => {
        return {
          name: file.name
        }
      })

      this.validateFiles({
        files: filesToValidate
      })
        .then(this.processValidateResponse)
        .catch(this.processValidateResponse)
        .finally(() => (this.filesIsLoading = false))
    },
    transactionItem (fileName) {
      return ChargebackMassRepresentmentManager.getTransactionItem(
        fileName,
        this.transactions
      )
    },
    submitFiles () {
      this.submitFilesIsLoading = true
      this.processFiles({
        files: this.files
      })
        .then(() => {
          this.sucessSubmitFiles = true
        })
        .finally(() => (this.submitFilesIsLoading = false))
    }
  }
}
</script>
<i18n>
{
  "pt-BR": {
    "send.document": "Enviar documentos",
    "paragraph.1": "Para abrir uma disputa em massa os documentos enviados,",
    "paragraph.2": " devem estar nomeados com o identificador da transação que deseja abrir a disputa.",
    "documents-list.link": "Confira todas as recomendações para o envio da documentação",
    "next": "Continuar",
    "total.size.error": "Os documentos anexados não podem exceder o limite de tamanho combinado de 50MB."
  },
  "en-US": {
    "send.document": "Send documents",
    "paragraph.1": "To open a dispute, the documents sent",
    "paragraph.2": " must be named with the identifier of the transaction you want to dispute.",
    "documents-list.link": "Check all recommendations for sending documentation",
    "next": "Next",
    "total.size.error": "The attached documents cannot exceed the combined size limit of 50MB."
  }
}
</i18n>
